import { useState, useContext, useEffect, Fragment } from "react"
import Link from "next/link"
import * as Sentry from "@sentry/nextjs"
import { useRouter } from "next/router"
import styled from "styled-components"
import { Row, Col, Spinner, Breadcrumb } from "react-bootstrap"
import { toast } from "react-toastify"
import { addEvent } from "@utils/gtm"
import { AppContext } from "AppContext"
import { CURRENT_USER } from "@modules/User/graphql"
import { Desktop, Mobile } from "@modules/Common/components/Device"
import { useStudentActivityPartialQuery, useUnlockStudentActivityMutation } from "@generated/graphql"
import { isEmptyString } from "@utils/stringUtils"
import { DISPLAY_STATE } from "../constants"

const StyledActivityHeader = styled.div`
  @media (max-width: 601px) {
    margin: 16px 24px;
  }
`

const StyledBadgeSpan = styled.span`
  font-weight: 700;
  background: url('/assets/svg/badge-background.svg');
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  margin: 5px;
  padding-left: 10px;
  padding-right: 10px;
  font-size: 1.3em;
`

const StyledSkillNameSpan = styled.div`
  text-align: center;
  display: inline-block;
  font-weight: 700;
  background:url(/assets/svg/ActivityDetailSkillName.svg);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  color: white;
  border-radius: 8px;
  margin: 5px;
  padding-left: 10px;
  padding-right: 10px;
  font-size: 1.3em;
`

const StyledOverlay = styled.div`
  border-radius: 16px;
  margin-top: 24px;
  margin-left: 16px;
  background: #EAEDEC;
  padding: 48px 24px 36px 48px;
  text-align: left;
  margin-bottom: 24px;
  @media (max-width: 601px) {
    padding: 24px 12px 18px 24px;
  }
`

const StyledBox = styled.div`
  position: absolute;
  text-align: left;
  font-weight: bolder;
  font-size: 1.5em;
  @media (max-width: 601px) {
    font-size: 1.2em;
    & > img {
      width: 90%;
    }
  }
`

const StyledBoxHeader = styled.div`
  position: absolute;
  top: 5px;
  left: 42px;
  color: white;
  text-transform: uppercase;
`

const StyledButton = styled.button`
  background: url(/assets/img/button.png);
  background-repeat: no-repeat;
  background-position: center;
  padding: 0.25em 1.5em;
  background-size: 100% 100%;
  border: none;
  font-weight: 700;
  font-size: 1.2em;
  margin: 0.75em 0;
`

interface IActivityLockView {
  slug: string
  displayState: DISPLAY_STATE
  setDisplayState: any
}

export default function ActivityLockView({ slug, displayState, setDisplayState }: IActivityLockView) {
  const router = useRouter()
  const { state } = useContext(AppContext)
  const [showBuyCoins, setShowBuyCoins] = useState(false)
  const [isLoadingUserAccessRequest, setIsLoadingUserAccessRequest] = useState(false)
  const [unlockAudio, setUnlockAudio] = useState<HTMLAudioElement | null>(null)

  useEffect(() => {
    setUnlockAudio(new Audio("/assets/sound/activity-unlock.mp3"))
  }, [])

  const { loading: activityPartialLoading, data: activityPartialData, error: activityPartialError } = useStudentActivityPartialQuery({
    variables: {
      where: { slug: slug }
    },
  })

  const [unlockStudentActivityMutation] = useUnlockStudentActivityMutation()

  function onPostAccessAccept() {
    setIsLoadingUserAccessRequest(true)
    unlockStudentActivityMutation({
      variables: {
        slug: slug
      },
      refetchQueries: [{
        query: CURRENT_USER
      }]
    }).then((res) => {
      unlockAudio?.play()
      setDisplayState(DISPLAY_STATE.QA)
      const activity = activityPartialData?.studentActivity
      addEvent({
        "event": "spend_virtual_currency",
        "user_id": state.user?.id,
        "value": activity?.coinsToSpend,
        "item_id": activity?.id,
        "item": activity?.slug,
        "type": "activity"
      })
      toast("Activity is unlocked!", {
        type: "success"
      })
    }).catch((error: Error) => {
      setIsLoadingUserAccessRequest(false)
      if (error.message.indexOf("Insufficient") >= 0) {
        setShowBuyCoins(true)
      } else {
        Sentry.captureException(error)
      }
      toast(error.message, {
        type: "error"
      })
    })
  }

  return (<>
    <Row>
      <Col xs={{ offset: 1 }} md={{ offset: 2 }}>
        <Breadcrumb>
          <Breadcrumb.Item linkAs={Link} href="/dashboard">Home</Breadcrumb.Item>
          <Breadcrumb.Item linkAs={Link} href="/skills">Skills</Breadcrumb.Item>
          <Breadcrumb.Item linkAs={Link} href={`/skills/${activityPartialData?.studentActivity?.skill?.slug}`}>{activityPartialData?.studentActivity?.skill?.name}</Breadcrumb.Item>
          <Breadcrumb.Item linkAs={Link} href={`/skills/${activityPartialData?.studentActivity?.slug}`} active>{activityPartialData?.studentActivity?.title}</Breadcrumb.Item>
        </Breadcrumb>
      </Col>
    </Row>
    <Row>
      <Col lg={{ span: 3, offset: 2 }} md={{ span: 6, offset: 2 }} sm={{ span: 8, offset: 2 }} xs={{ span: 8, offset: 1 }} style={{ textAlign: "right" }}>
        <img src={activityPartialData?.studentActivity?.image || activityPartialData?.studentActivity?.featuredImage || "/assets/img/logo.png"} style={{ maxWidth: "240px" }} />
      </Col>
      <Col lg={{ span: 4 }} style={{ textAlign: "left", position: "relative" }}>
        <StyledActivityHeader>
          <h1>{activityPartialData?.studentActivity?.title}</h1>
          {!isEmptyString(activityPartialData?.studentActivity?.shortDescription) &&
            <div dangerouslySetInnerHTML={{ __html: activityPartialData?.studentActivity?.shortDescription || "" }} />}
          <div style={{ marginTop: "20px" }}>
            <StyledBadgeSpan>+{(activityPartialData?.studentActivity?.xps || 0) + (activityPartialData?.studentActivity?.questionXps || 0)}XPs</StyledBadgeSpan>
            <StyledBadgeSpan>{activityPartialData?.studentActivity?.difficultyLevel}</StyledBadgeSpan>
            <StyledBadgeSpan><img src="/assets/svg/ActivityDetailMinTag.svg" width="16px" />{activityPartialData?.studentActivity?.timeRequired}</StyledBadgeSpan>
          </div>
          <br />
          <div>Tags: {activityPartialData?.studentActivity?.tags?.map((tag, index) => (<Fragment key={tag.id}>{(index ? " , " : "")}<Link href={`/tags/${tag.slug}`}>{tag.name}</Link></Fragment>))}</div>
        </StyledActivityHeader>
      </Col>
    </Row>
    {displayState === DISPLAY_STATE.LOCK ?
      <Row>
        <Col lg={{ span: 8, offset: 2 }} className="mt-5">
          <StyledBox>
            <StyledBoxHeader>Unlock Activity</StyledBoxHeader>
            <img src="/assets/svg/ActivityDetailKnowMoreAboutIt.svg" />
          </StyledBox>
          <StyledOverlay>{activityPartialData?.studentActivity?.coinsToSpend === 0 ?
            <p style={{ fontSize: "1.3em" }}>Do you want to unlock the activity for <strong>FREE</strong>?</p>
            : <p style={{ fontSize: "1.3em" }}>Do you want to unlock the activity by spending <strong>{activityPartialData?.studentActivity?.coinsToSpend} coins?</strong></p>}
            {showBuyCoins ? <>
              <Desktop>
                <StyledButton disabled={isLoadingUserAccessRequest} onClick={() => {
                  router.push("/purchase/coins")
                }}>Buy Coins</StyledButton>
              </Desktop>
              <Mobile>
                <h3>Recharge coins via myskillshaala website or contact us on WhatsApp</h3>
              </Mobile>
            </> : <StyledButton disabled={isLoadingUserAccessRequest} onClick={() => { onPostAccessAccept() }}>
              {isLoadingUserAccessRequest && <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" color="white" />}
              Yes</StyledButton>}
          </StyledOverlay>
        </Col>
      </Row>
      : <Row>
        <Col>
          <br />
          <br />
          <h4>You&apos;re back..! Continue to complete the activity.</h4>
          <StyledButton onClick={() => setDisplayState(DISPLAY_STATE.QA)}>Continue</StyledButton>
          <br />
          <br />
          <br />
        </Col>
      </Row>}
  </>
  )
}
